import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import {
  Container,
  Card,
  Button,
  Alert,
  Row,
  Col
} from 'react-bootstrap';

export default function BookingSuccess() {
  const location = useLocation();
  const navigate = useNavigate();
  const { leadId, bookingId, driverName, driverFullName, driverPhone, companyName, companyLogo, bookingLinkName } = location.state || {};
  
  // State for job acceptance
  const [isJobAccepted, setIsJobAccepted] = useState(false);
  const [audioPlayed, setAudioPlayed] = useState(false);

  // Function to generate vCard for adding to contacts
  const generateVCard = () => {
    const vcard = `BEGIN:VCARD
VERSION:3.0
FN:${driverFullName || driverName}
N:${(driverFullName || driverName).split(' ').reverse().join(';')}
ORG:${companyName}
TEL;TYPE=CELL:${driverPhone || ''}
END:VCARD`;

    const blob = new Blob([vcard], { type: 'text/vcard' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${driverFullName || driverName} - ${companyName}.vcf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Socket.io connection for real-time job acceptance notifications
  useEffect(() => {
    if (!leadId) return;

    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    const newSocket = io(baseUrl, {
      transports: ['websocket', 'polling'],
      path: '/socket.io',
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelayMax: 5000
    });

    // Debug socket connection
    newSocket.on('connect', () => {
      console.log('🔌 Socket connected to backend:', baseUrl);
    });

    newSocket.on('disconnect', () => {
      console.log('🔌 Socket disconnected from backend');
    });

    newSocket.on('connect_error', (error) => {
      console.error('🔌 Socket connection error:', error);
    });

    // Listen for job acceptance events
    newSocket.on('job-accepted', (data) => {
      console.log('Job acceptance event received:', data);
      if (data.leadId === leadId) {
        setIsJobAccepted(true);
        console.log('🎉 Job accepted! Updating UI...');
      }
    });

    // Listen for booking link job acceptance (from our new backend event)
    newSocket.on('booking-job-accepted', (data) => {
      console.log('Booking job acceptance event received:', data);
      if (data.leadId === leadId) {
        setIsJobAccepted(true);
        console.log('🎉 Booking job accepted! Updating UI...');
      }
    });

    // Cleanup on unmount
    return () => {
      newSocket.disconnect();
    };
  }, [leadId]);

  // Play congratulations audio when job is accepted
  useEffect(() => {
    if (isJobAccepted && !audioPlayed) {
      try {
        const audio = new Audio('/audio/Congratulations.mp3');
        audio.volume = 0.7; // Set volume to 70%
        audio.play()
          .then(() => {
            console.log('🎉 Congratulations audio played successfully');
            setAudioPlayed(true);
          })
          .catch(error => {
            console.warn('Could not play congratulations audio:', error);
            // Don't show error to user, just log it
          });
      } catch (error) {
        console.error('Error loading congratulations audio:', error);
      }
    }
  }, [isJobAccepted, audioPlayed]);

  if (!leadId) {
    return (
      <Container className="mt-5">
        <Alert variant="warning">
          <h4>Invalid Access</h4>
          <p>This page is only accessible after a successful booking submission.</p>
          <Button variant="primary" onClick={() => navigate('/')}>
            Return to Home
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-5">
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <Card className="text-center">
            <Card.Body className="p-5">
              {/* Taxi-Local.uk Heading */}
              <div className="mb-4">
                <h2 className="text-primary mb-3">
                  <strong>Taxi-Local.uk</strong>
                </h2>
              </div>

              <div className="mb-4">
                <div className="text-success mb-3">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                  </svg>
                </div>
                {isJobAccepted ? (
                  <h2 className="text-success">Great News, Your Job has been Accepted!</h2>
                ) : (
                  <h2 className="text-success">Booking Request Submitted!</h2>
                )}
              </div>

              {/* Company Information */}
              <div className="mb-4">
                {companyLogo && (
                  <div className="mb-3">
                    <img 
                      src={companyLogo} 
                      alt={`${companyName} logo`}
                      style={{ maxHeight: '80px', maxWidth: '200px' }}
                      className="img-fluid"
                    />
                  </div>
                )}
                <h4>{companyName}</h4>
                {bookingLinkName && (
                  <p className="text-muted mb-0">{bookingLinkName}</p>
                )}
              </div>

                      {isJobAccepted ? (
                        <Alert variant="success">
                          <h5>🎉 Congratulations!</h5>
                          <div className="text-start">
                            <p><strong>✅ Job Accepted:</strong> Your booking has been confirmed by {driverName}!</p>
                            <p><strong>📧 Confirmation Email:</strong> You should receive a confirmation email shortly with all booking details.</p>
                            <p><strong>📞 Driver Contact:</strong> Your driver may contact you to discuss any specific requirements or confirm pickup details.</p>
                            <p className="mb-0 text-success"><strong>✓ Confirmed:</strong> Your booking is now confirmed and scheduled!</p>
                          </div>
                        </Alert>
                      ) : (
                        <Alert variant="info">
                          <h5>📋 What Happens Next?</h5>
                          <div className="text-start">
                            {location.state?.whatToExpect ? (
                              <div dangerouslySetInnerHTML={{ __html: location.state.whatToExpect.replace(/\n/g, '<br>') }} />
                            ) : (
                              <>
                                <p><strong>1. Driver Review:</strong> Your booking request has been sent to our driver for review.</p>
                                <p><strong>2. Driver Contact:</strong> The driver depending if currently available,(eg not Driving), will review request. If Request fits in with current Schedule will immediately "Accept" and a Confirmation email will be sent, OR! the Driver may contact you to discuss options.</p>
                                <p><strong>3. Confirmation:</strong> Once the driver accepts your booking, you'll receive a confirmation email with all details.</p>
                                <p className="mb-0 text-warning"><strong>⚠️ Important:</strong> This is not a confirmed booking yet. The driver needs to accept your request first.</p>
                              </>
                            )}
                          </div>
                        </Alert>
                      )}

                      {/* Important note about booking variations */}
                      <Alert variant="warning" className="mb-4">
                        <strong>⚠️ Important:</strong> Any variation to this booking must be made <strong>verbally to the driver directly</strong>. 
                        Please <strong>do not text, email or leave answerphone messages</strong> as drivers may not be able to respond 
                        immediately if the booking is imminent.
                      </Alert>

              <div className="mb-4">
                <h5>Booking Details</h5>
                <p className="text-muted">
                  <strong>Booking ID:</strong> <span className="badge bg-primary" style={{ fontSize: '0.9rem' }}>{bookingId || leadId}</span><br/>
                  <strong>Company:</strong> {companyName}<br/>
                  <strong>Driver:</strong> {driverName}<br/>
                  {driverPhone && (
                    <>
                      <strong>Phone:</strong> <a href={`tel:${driverPhone}`} className="text-decoration-none">{driverPhone}</a><br/>
                    </>
                  )}
                  {bookingLinkName && (
                    <>
                      <strong>Service:</strong> {bookingLinkName}
                    </>
                  )}
                </p>
                
                {/* Add to Contacts Button */}
                {driverPhone && (
                  <div className="mb-3">
                    <Button 
                      variant="outline-primary" 
                      size="sm"
                      onClick={generateVCard}
                      className="w-100"
                    >
                      📇 Add Driver to Contacts
                    </Button>
                  </div>
                )}
              </div>

              {isJobAccepted ? (
                <Alert variant="success">
                  <h6>✅ Booking Confirmed!</h6>
                  <p className="mb-0">
                    Your booking has been accepted and confirmed. You should receive a confirmation email shortly with all the details.
                  </p>
                </Alert>
              ) : (
                <Alert variant="warning">
                  <h6>📞 Expect a Call Soon</h6>
                  <p className="mb-0">
                    Our driver will contact you shortly to discuss your booking. 
                    Please ensure your phone is available for the call.
                  </p>
                </Alert>
              )}

              <div className="d-grid gap-2">
                <Button 
                  variant="primary" 
                  size="lg"
                  onClick={() => navigate('/')}
                >
                  Return to Home
                </Button>
                <Button 
                  variant="outline-secondary"
                  onClick={() => window.print()}
                >
                  Print This Page
                </Button>
              </div>

              <div className="mt-4 text-muted">
                <small>
                  {isJobAccepted ? (
                    <>
                      <strong>Confirmed Booking:</strong><br/>
                      • Your booking is confirmed and scheduled<br/>
                      • Confirmation email sent to your inbox<br/>
                      • Keep this booking ID for reference<br/>
                      • Contact {companyName} if you have questions
                    </>
                  ) : (
                    <>
                      <strong>Next Steps:</strong><br/>
                      • Driver will call you within 30 minutes<br/>
                      • Email confirmation will be sent if accepted<br/>
                      • Keep this booking ID for reference<br/>
                      • Contact {companyName} if you have questions
                    </>
                  )}
                </small>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
